import { useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { FillBar } from "@teamna/shared";
import { FillBarTrack } from "../fill-bar.tsx";
import { layoutDay, shortTimeLabel, snap, SNAP_MIN, timeLabel } from "./layout.ts";

/**
 * The calendar of the owner's Schedule page, in both of its views: the sessions of one week (dates)
 * and the weekly timetable (weekdays). One look and one feel for both, so the two views read as one
 * page.
 *
 * - One column per day, the hours down the left, one block per session in the colour of its level.
 *   The board always has the whole day, midnight to midnight, so a session can go at any time. It
 *   opens on the usual hours (`usualHours`), and the owner scrolls for the early and late hours.
 *   Blocks at the same time sit side by side. Free hours for private sessions are thin striped strips
 *   at the left of the day.
 * - A click on a block opens it (`onSelect`). A movable block follows the pointer: up or down
 *   changes the time, sideways changes the day. The grip at its lower edge changes the length.
 *   The board says where the block goes (`onDragMove`, `onDragEnd`); the page decides what that
 *   means and draws the blocks again.
 * - `renderColumn` draws what one view puts on a day under the blocks (the timetable's add layer and
 *   dashed days). `renderBlockExtra` draws next to a block (the timetable's ✕).
 * - `hoverPreview`: the mouse rests on a block that cuts some of its text, and a copy of it floats
 *   over it at full size, with nothing cut (2026-10-21). A block that shows all of its text gets no
 *   copy. The view turns it off while the owner adds or changes something.
 */

/** How long the mouse rests on a block before its full copy shows. */
const PEEK_DELAY_MS = 250;

export type CalendarColumn = {
  key: string;
  /** "Saturday", or "Sat 17". */
  title: string;
  /** "2 sessions", or "Day off". */
  subtitle: string;
  /** The accessible name of the day: "Saturday, 2 weekly times". */
  label: string;
  off: boolean;
  today?: boolean;
  /** Above the title: the coach's photo on the Day view, the same face as the coach strip. */
  avatar?: ReactNode;
};

export type CalendarBlock = {
  /** Unique on the board. */
  key: string;
  /** What `onSelect` and the drag callbacks get: a block can show on several days. */
  id: string;
  column: string;
  start: number;
  end: number;
  color: string;
  /** The accessible name. */
  label: string;
  /** The three lines: "3 PM", "Golden team", "3/8 Omar Coach". */
  time: string;
  title: string;
  detail?: string | null;
  /**
   * The places on the third line, before `detail` and never cut: "3/3 +2 waiting" (D38). In a
   * narrow block the name in `detail` is cut first.
   */
  count?: string | null;
  /** Free hours for private sessions: a thin striped strip with its words on its side. */
  free?: boolean;
  /** Faded and struck through, opens on a click, never moves: a time that comes off, a cancelled session. */
  struck?: boolean;
  selected?: boolean;
  /** A problem: a red edge. */
  flagged?: boolean;
  /** Another person is picked on the coach row. */
  faded?: boolean;
  movable?: boolean;
  resizable?: boolean;
  /** The step of a length change, in minutes (15 by default). A move is always 15. */
  step?: number;
  /** Takes no pointer at all: the week of sessions while a level is picked, so a click on it adds a session. */
  inert?: boolean;
  /** Dashed and not usable: the month before, shown on an empty month as the start of a copy. */
  placeholder?: boolean;
  /**
   * The fill bar along the lower edge (D38): booked of the places, gold when people wait. A screen
   * reader reads its words (`text`) as the description of the block, after `label`.
   */
  meter?: FillBar | null;
};

/** Where a dragged block goes: its day, its start and its end. */
export type CalendarChange = { column: string; start: number; end: number };
export type CalendarDragMode = "move" | "resize";

/** What a `renderColumn` needs to draw on a day. */
export type ColumnContext = {
  perMinute: number;
  /** The width the free hours take at the left of the day, in pixels. */
  inset: number;
  range: { start: number; end: number };
  /** The minute under a point of the day, snapped, so that a block of `length` stays on the board. */
  minuteAt: (clientY: number, top: number, length: number) => number;
};

/** The hours the board draws: the whole day. */
export const WHOLE_DAY = { start: 0, end: 24 * 60 };

const FREE_WIDTH = 22;
const EDGE_PX = 7;

type Drag = {
  block: CalendarBlock;
  mode: CalendarDragMode;
  x: number;
  y: number;
  moved: boolean;
};

export function CalendarBoard({
  columns,
  blocks,
  usualHours,
  hourHeight = 72,
  nowMinute,
  showNow = false,
  minWidth = 760,
  onSelect,
  canDrop = () => true,
  onDragStart,
  onDragMove,
  onDragEnd,
  renderColumn,
  renderBlockExtra,
  hoverPreview = false,
}: {
  columns: CalendarColumn[];
  blocks: CalendarBlock[];
  /** The hours the club usually works, with all its blocks: the board opens on them, not at midnight. */
  usualHours: { start: number; end: number };
  hourHeight?: number;
  /** Minutes after midnight, now, in the business time zone: the board opens there, in the usual hours. */
  nowMinute?: number;
  /** Draw a line across the board at `nowMinute` (the Day view, on today). Off by default. */
  showNow?: boolean;
  /** The narrowest the board gets before it scrolls sideways: 760px for a week of seven days. */
  minWidth?: number;
  onSelect: (id: string) => void;
  /** May the block go to this day? (A weekly time cannot take a day it already has.) */
  canDrop?: (block: CalendarBlock, column: string) => boolean;
  onDragStart?: (block: CalendarBlock, mode: CalendarDragMode) => void;
  onDragMove?: (block: CalendarBlock, change: CalendarChange, mode: CalendarDragMode) => void;
  /**
   * The pointer let go. A drag that went nowhere gives no call: it was a click. A cancelled drag
   * (pointercancel, the window lost focus) ends at the block's own place: no change.
   */
  onDragEnd?: (block: CalendarBlock, change: CalendarChange, mode: CalendarDragMode) => void;
  renderColumn?: (column: CalendarColumn, context: ColumnContext) => ReactNode;
  renderBlockExtra?: (block: CalendarBlock, position: { top: number; left: string }) => ReactNode;
  /** Show a block's full copy when the mouse rests on it. Off while the view adds or changes something. */
  hoverPreview?: boolean;
}) {
  const perMinute = hourHeight / 60;
  const root = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const latest = useRef<CalendarChange | null>(null);
  // The block whose length is changing: it shows its minutes while the owner pulls.
  const [resizing, setResizing] = useState<{ key: string; minutes: number } | null>(null);
  // The block under the mouse, with where it is on the screen: its full copy floats there. The block
  // is read again from `blocks` on each render, so new data shows, and a block that went has no copy.
  const [peek, setPeek] = useState<{ key: string; rect: DOMRect } | null>(null);
  const peekBlock = peek ? blocks.find((block) => block.key === peek.key) : undefined;
  const peekTimer = useRef<number | undefined>(undefined);
  // The block buttons by key, to measure the block under the copy again.
  const blockButtons = useRef(new Map<string, HTMLButtonElement>());
  // The ids of the fill bar words, unique on the page (two boards can show the same session).
  const boardId = useId();
  // New data can move or resize the block (another time, another length, a new block next to it):
  // after each render the copy takes the block's place again, or goes with the block.
  useLayoutEffect(() => {
    if (!peek) return;
    const button = blockButtons.current.get(peek.key);
    if (!button) {
      setPeek(null);
      return;
    }
    const rect = button.getBoundingClientRect();
    const old = peek.rect;
    if (rect.top !== old.top || rect.left !== old.left || rect.width !== old.width || rect.height !== old.height) {
      setPeek({ key: peek.key, rect });
    }
  }, [peek, blocks, columns]);
  function endPeek() {
    window.clearTimeout(peekTimer.current);
    setPeek(null);
  }
  // The view went into an add or edit mode: no copy stays on the screen. Set during render, React's
  // way to adjust state to new props.
  if (!hoverPreview && peek) setPeek(null);
  // A copy still waiting for its delay does not open later in that mode either.
  useEffect(() => {
    if (!hoverPreview) window.clearTimeout(peekTimer.current);
  }, [hoverPreview]);
  // The copy is fixed on the screen, so a scroll or a resize would leave it behind: it goes, and a
  // copy still waiting for its delay does not open at the old place.
  useEffect(() => {
    const hide = () => {
      window.clearTimeout(peekTimer.current);
      setPeek(null);
    };
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, []);
  useEffect(() => () => window.clearTimeout(peekTimer.current), []);

  const range = WHOLE_DAY;

  // Once, when the board opens: the hour before now sits under the day names, but the screen stays
  // in the usual hours (at 11 PM it shows the last of them, not an empty night). With no now, the
  // usual hours start at the top. Later renders (a drag, a new block) never move the owner's scroll.
  useEffect(() => {
    const scroller = root.current?.parentElement;
    if (!scroller) return;
    // The day names stay on top of the hours, so they take some of the screen.
    const header = root.current?.firstElementChild?.getBoundingClientRect().height ?? 0;
    const visible = (scroller.clientHeight - header) / perMinute;
    const wanted = nowMinute === undefined ? usualHours.start : Math.min(nowMinute - 60, usualHours.end - visible);
    const minute = Math.max(usualHours.start, Math.min(range.end, wanted));
    scroller.scrollTop = (minute - range.start) * perMinute;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const height = (range.end - range.start) * perMinute;

  function minuteAt(clientY: number, top: number, length: number): number {
    const raw = range.start + (clientY - top) / perMinute;
    return Math.max(range.start, Math.min(range.end - length, snap(raw)));
  }

  function columnAt(clientX: number, clientY: number): string | null {
    if (typeof document.elementFromPoint !== "function") return null;
    const element = document.elementFromPoint(clientX, clientY);
    const column = element instanceof HTMLElement ? element.closest<HTMLElement>("[data-column]") : null;
    return column?.dataset.column ?? null;
  }

  function onPointerMove(event: PointerEvent) {
    const current = drag.current;
    if (!current) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (!current.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
    const block = current.block;
    if (!current.moved) {
      current.moved = true;
      onDragStart?.(block, current.mode);
    }
    const step = block.step ?? SNAP_MIN;
    let change: CalendarChange;
    if (current.mode === "resize") {
      const end = Math.max(block.start + step, Math.min(range.end, block.end + snap(dy / perMinute, step)));
      change = { column: block.column, start: block.start, end };
      setResizing({ key: block.key, minutes: end - block.start });
    } else {
      const length = block.end - block.start;
      const start = Math.max(range.start, Math.min(range.end - length, block.start + snap(dy / perMinute, SNAP_MIN)));
      let column = block.column;
      const target = columnAt(event.clientX, event.clientY);
      const day = columns.find((row) => row.key === target);
      if (day && target !== block.column && !day.off && canDrop(block, day.key)) column = day.key;
      change = { column, start, end: start + length };
    }
    latest.current = change;
    onDragMove?.(block, change, current.mode);
  }

  function stopListening() {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerCancel);
    window.removeEventListener("blur", onPointerCancel);
  }

  /**
   * The browser took the pointer (a touch scroll, a system dialog) or the window lost focus: the
   * block goes back where it was, and the page gets a drag that ends there, so it clears its state.
   */
  function onPointerCancel() {
    stopListening();
    const current = drag.current;
    drag.current = null;
    setResizing(null);
    if (!current?.moved) return;
    const { block } = current;
    const back = { column: block.column, start: block.start, end: block.end };
    onDragMove?.(block, back, current.mode);
    onDragEnd?.(block, back, current.mode);
  }

  function onPointerUp() {
    stopListening();
    const current = drag.current;
    drag.current = null;
    setResizing(null);
    if (!current) return;
    if (!current.moved) {
      onSelect(current.block.id);
      return;
    }
    onDragEnd?.(current.block, latest.current ?? { column: current.block.column, start: current.block.start, end: current.block.end }, current.mode);
  }

  function startDrag(event: ReactPointerEvent<HTMLButtonElement>, block: CalendarBlock) {
    if (event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    // The edge that changes the length: the grip at the bottom of a block, or the last pixels of it.
    // A free strip is drawn upside down, so its grip is at its top in the code and at its bottom on
    // screen; the rect is the screen.
    const onGrip = event.target instanceof Element && event.target.closest("[data-grip]") !== null;
    const nearEdge = block.resizable === true && (onGrip || (rect.height > 0 && rect.bottom - event.clientY <= EDGE_PX));
    if (!nearEdge && !block.movable) return;
    drag.current = { block, mode: nearEdge ? "resize" : "move", x: event.clientX, y: event.clientY, moved: false };
    latest.current = null;
    event.preventDefault();
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("blur", onPointerCancel);
  }

  const hours: number[] = [];
  for (let minute = range.start; minute < range.end; minute += 60) hours.push(minute);
  // One column per day of a week, or one per coach on the Day view.
  const grid = `52px repeat(${columns.length}, minmax(0, 1fr))`;

  return (
    <div ref={root} style={{ minWidth }}>
      <div className="sticky top-0 z-20 grid border-b border-line bg-paper" style={{ gridTemplateColumns: grid }}>
        <div aria-hidden="true" />
        {columns.map((column) => (
          <div key={column.key} className={["border-l border-line px-1 py-2 text-center", column.off ? "text-ink-3" : ""].join(" ")}>
            {column.avatar ? <div className="mb-1 flex justify-center">{column.avatar}</div> : null}
            <p
              dir="auto"
              className={[
                "text-[12.5px] font-semibold",
                column.today ? "mx-auto w-fit rounded-full bg-accent px-2 text-inverse" : "text-ink-2",
              ].join(" ")}
            >
              {column.title}
            </p>
            <p className="text-[11px] text-ink-3 tabular-nums">{column.subtitle}</p>
          </div>
        ))}
      </div>

      <div className="relative grid" style={{ height, gridTemplateColumns: grid }}>
        <div aria-hidden="true" className="relative">
          {hours.slice(1).map((minute) => (
            <span
              key={minute}
              className="absolute right-2 -translate-y-1/2 whitespace-nowrap text-[11px] text-ink-3 tabular-nums"
              style={{ top: (minute - range.start) * perMinute }}
            >
              {shortTimeLabel(minute)}
            </span>
          ))}
        </div>

        {columns.map((column) => {
          const own = blocks.filter((block) => block.column === column.key);
          const { placed, freeLanes } = layoutDay(own.map((block) => ({ key: block.key, start: block.start, end: block.end, free: block.free === true })));
          const inset = freeLanes * (FREE_WIDTH + 2);

          return (
            <section
              key={column.key}
              data-column={column.key}
              aria-label={column.label}
              className={["relative border-l border-line", column.today ? "bg-accent-surface/25" : ""].join(" ")}
              style={{
                backgroundImage: column.off
                  ? "repeating-linear-gradient(135deg, var(--color-sunken) 0 6px, transparent 6px 12px)"
                  : `repeating-linear-gradient(to bottom, transparent 0, transparent ${hourHeight / 2 - 1}px, var(--color-line) ${hourHeight / 2 - 1}px, var(--color-line) ${hourHeight / 2}px)`,
              }}
            >
              {renderColumn?.(column, { perMinute, inset, range, minuteAt })}

              {own.map((block) => {
                const spot = placed.get(block.key)!;
                const top = (block.start - range.start) * perMinute + 1;
                const blockHeight = Math.max(18, (block.end - block.start) * perMinute - 2);
                const short = blockHeight < 44;
                const grabs = block.movable === true || block.resizable === true;
                const position = block.free
                  ? { left: 3 + spot.freeLane * (FREE_WIDTH + 2), width: FREE_WIDTH }
                  : {
                      left: `calc(${inset}px + (100% - ${inset}px) * ${spot.lane / spot.of} + 3px)`,
                      width: `calc((100% - ${inset}px) / ${spot.of} - 6px)`,
                    };
                return (
                  <span key={block.key}>
                    <button
                      ref={(button) => {
                        if (button) blockButtons.current.set(block.key, button);
                        else blockButtons.current.delete(block.key);
                      }}
                      type="button"
                      aria-label={block.label}
                      aria-describedby={block.meter && !block.free ? `${boardId}-fill-${block.key}` : undefined}
                      aria-pressed={block.selected === true}
                      data-block={block.id}
                      disabled={block.placeholder}
                      onPointerDown={(event) => {
                        endPeek();
                        if (grabs) startDrag(event, block);
                      }}
                      onPointerEnter={(event) => {
                        // A mouse only: a finger has no hover, and a drag in progress is not a rest.
                        if (!hoverPreview || event.pointerType !== "mouse" || drag.current) return;
                        const target = event.currentTarget;
                        // Only when the block cuts some of its text: else the copy says nothing new.
                        if (!textIsCut(target, short && !block.free && Boolean(block.detail || block.count))) return;
                        window.clearTimeout(peekTimer.current);
                        peekTimer.current = window.setTimeout(
                          () => setPeek({ key: block.key, rect: target.getBoundingClientRect() }),
                          PEEK_DELAY_MS,
                        );
                      }}
                      onPointerLeave={endPeek}
                      onClick={(event) => {
                        // A pointer press on a block that moves is handled on pointer up (a drag is
                        // not a click). A keyboard press, or a block that never moves, opens it here.
                        if (!grabs || event.detail === 0) onSelect(block.id);
                      }}
                      className={[
                        "group absolute z-[2] overflow-hidden rounded-lg text-left leading-[1.25] touch-none select-none",
                        "focus:outline-none focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-accent-soft",
                        block.free ? "border-[1.5px] border-dashed px-0.5 py-1.5 [writing-mode:vertical-rl] rotate-180 text-[10.5px]" : "border-l-[3px] px-1.5 py-1 text-[11px]",
                        block.struck ? "cursor-pointer opacity-50 line-through" : grabs ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
                        block.inert ? "pointer-events-none" : "",
                        block.placeholder ? "pointer-events-none border-[1.5px] border-dashed opacity-70" : "",
                        block.selected ? "shadow-[0_0_0_2px_var(--color-ink)]" : "",
                        block.faded ? "opacity-20" : "",
                        block.flagged ? "shadow-[0_0_0_2px_var(--color-cancelled)]" : "",
                        short && !block.free ? "flex items-center gap-1.5" : "",
                      ].join(" ")}
                      style={{
                        top,
                        height: blockHeight,
                        ...position,
                        borderColor: block.color,
                        color: "var(--color-ink)",
                        background: block.free
                          ? `repeating-linear-gradient(135deg, color-mix(in srgb, ${block.color} 22%, var(--color-paper)) 0 7px, color-mix(in srgb, ${block.color} 8%, var(--color-paper)) 7px 14px)`
                          : `color-mix(in srgb, ${block.color} ${block.placeholder ? 5 : 14}%, var(--color-paper))`,
                      }}
                    >
                      {block.free ? (
                        <span className="whitespace-nowrap">
                          <b dir="auto" className="font-semibold">{block.title}</b> {block.time}
                          {block.detail ? `, ${block.detail}` : ""}
                        </span>
                      ) : (
                        <>
                          <span className="block shrink-0 whitespace-nowrap font-semibold tabular-nums">{block.time}</span>
                          <span dir="auto" className="block truncate font-semibold">{block.title}</span>
                          {short || (!block.detail && !block.count) ? null : (
                            <span className="flex min-w-0 gap-1 text-ink-2">
                              {block.count ? (
                                <span data-block-count className="shrink-0 whitespace-nowrap font-semibold tabular-nums text-ink">
                                  {block.count}
                                </span>
                              ) : null}
                              {block.detail ? (
                                <span dir="auto" className="min-w-0 truncate">
                                  {block.detail}
                                </span>
                              ) : null}
                            </span>
                          )}
                        </>
                      )}
                      {block.meter && !block.free ? (
                        <>
                          <span id={`${boardId}-fill-${block.key}`} hidden>
                            {block.meter.text}
                          </span>
                          <FillBarTrack bar={block.meter} className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-[color-mix(in_srgb,var(--color-ink)_12%,transparent)]" />
                        </>
                      ) : null}
                      {block.resizable ? (
                        // The grip that changes the length: a line at the bottom on hover or when picked.
                        <span
                          data-grip
                          aria-hidden="true"
                          title="Drag to change the length"
                          className={["absolute inset-x-0 grid h-2 cursor-ns-resize place-items-center", block.free ? "top-0" : "bottom-0"].join(" ")}
                        >
                          <span
                            className={[
                              "rounded-full bg-ink-3 transition-opacity",
                              block.free ? "h-[3px] w-3" : "h-[3px] w-6",
                              block.selected || resizing?.key === block.key ? "opacity-70" : "opacity-0 group-hover:opacity-70",
                            ].join(" ")}
                          />
                        </span>
                      ) : null}
                      {resizing?.key === block.key && !block.free ? (
                        <span className="pointer-events-none absolute right-1 bottom-2 rounded-full bg-ink px-1.5 text-[10.5px] font-bold text-inverse tabular-nums">
                          {resizing.minutes} min
                        </span>
                      ) : null}
                    </button>
                    {renderBlockExtra?.(block, { top, left: `calc(${inset}px + (100% - ${inset}px) * ${(spot.lane + 1) / spot.of} - 24px)` })}
                  </span>
                );
              })}
            </section>
          );
        })}
        {showNow && nowMinute !== undefined && nowMinute >= range.start && nowMinute < range.end ? (
          <div
            aria-hidden="true"
            data-now
            className="pointer-events-none absolute right-0 left-[46px] z-10 flex -translate-y-1/2 items-center"
            style={{ top: (nowMinute - range.start) * perMinute }}
          >
            <span className="size-2.5 rounded-full bg-cancelled" />
            <span className="h-0.5 flex-1 bg-cancelled" />
          </div>
        ) : null}
      </div>
      {peek && peekBlock && hoverPreview ? createPortal(<BlockPeek block={peekBlock} rect={peek.rect} />, document.body) : null}
    </div>
  );
}

/**
 * Does the block cut some of its text? Measured when the mouse arrives, so it follows the window and
 * the column width: a line cut with "…", text taller or wider than the block, or a detail line a
 * short block leaves out (`hidesDetail`).
 */
function textIsCut(button: HTMLElement, hidesDetail: boolean): boolean {
  if (hidesDetail) return true;
  if (button.scrollHeight > button.clientHeight + 1 || button.scrollWidth > button.clientWidth + 1) return true;
  // The third line is a row: the count, then the name that is cut first. Measure both.
  return Array.from(button.querySelectorAll<HTMLElement>(":scope > span, :scope > span > span")).some((line) => line.scrollWidth > line.clientWidth + 1);
}

/** The widest the full copy of a block gets. */
const PEEK_MAX_WIDTH = 280;

/**
 * The full copy of a block, over the block itself: the same colours, at least as wide, with the end
 * time and with no text cut. Fixed on the screen (a portal), so the board's scroll never clips it.
 * Near the right or the bottom edge it grows the other way. The mouse goes through it, and a screen
 * reader skips it: the block's own name already says all of it.
 */
function BlockPeek({ block, rect }: { block: CalendarBlock; rect: DOMRect }) {
  const toLeft = rect.left + Math.max(rect.width, PEEK_MAX_WIDTH) > window.innerWidth - 8;
  const upward = rect.top + 140 > window.innerHeight;
  return (
    <div
      aria-hidden="true"
      data-block-peek={block.id}
      className={[
        "pointer-events-none fixed z-[60] rounded-lg border-l-[3px] px-2 py-1.5 text-[12.5px] leading-[1.35] text-ink",
        "shadow-[0_14px_36px_-12px_rgba(17,26,43,0.45),0_0_0_1px_rgba(17,26,43,0.08)]",
        block.struck ? "line-through" : "",
      ].join(" ")}
      style={{
        ...(toLeft ? { right: window.innerWidth - rect.right } : { left: rect.left }),
        ...(upward ? { bottom: window.innerHeight - rect.bottom } : { top: rect.top }),
        minWidth: rect.width,
        maxWidth: PEEK_MAX_WIDTH,
        width: "max-content",
        minHeight: rect.height,
        borderColor: block.color,
        background: `color-mix(in srgb, ${block.color} 14%, var(--color-paper))`,
      }}
    >
      <p className="font-semibold tabular-nums">
        {timeLabel(block.start)} to {timeLabel(block.end)}
      </p>
      <p dir="auto" className="font-semibold">
        {block.title}
      </p>
      {block.detail || block.count ? (
        <p dir="auto" className="text-ink-2">
          {[block.count, block.detail].filter(Boolean).join(" ")}
        </p>
      ) : null}
    </div>
  );
}
